import React, { useState, useEffect } from 'react';
import { ChevronLeft, Calendar, Search, X, Loader2, Package } from 'lucide-react';
import axios from 'axios';
import ProvinciaDeliveriesTable from '../ProvinciaManager/ProvinciaDeliveriesTable';

const API_URL = process.env.REACT_APP_API_URL;

function AdminProvinciaDeliveries({ setActiveComponent }) {
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [deliveries, setDeliveries] = useState([]);
  const [filteredDeliveries, setFilteredDeliveries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    if (selectedDate) {
      fetchDeliveries();
    }
  }, [selectedDate]);

  useEffect(() => {
    filterDeliveries();
  }, [searchTerm, deliveries]);

  async function fetchDeliveries() {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/admin/provincia-deliveries`, {
        params: { date: selectedDate }
      });
      setDeliveries(response.data.deliveries || []);
    } catch (error) {
      console.error('Error fetching provincia deliveries:', error);
    } finally {
      setLoading(false);
    }
  }

  function filterDeliveries() {
    if (!searchTerm.trim()) {
      setFilteredDeliveries(deliveries);
      return;
    }

    const searchLower = searchTerm.toLowerCase().trim();
    const filtered = deliveries.filter(delivery => {
      return (
        delivery.id.toString().includes(searchLower) ||
        delivery.recipient_name?.toLowerCase().includes(searchLower) ||
        delivery.recipient_phone?.includes(searchLower) ||
        delivery.recipient_dni?.includes(searchLower) ||
        delivery.province?.toLowerCase().includes(searchLower) ||
        delivery.agency_address?.toLowerCase().includes(searchLower)
      );
    });
    setFilteredDeliveries(filtered);
  }

  function handleDeliveryUpdate(updatedDelivery) {
    setDeliveries(prev =>
      prev.map(d => (d.id === updatedDelivery.id ? updatedDelivery : d))
    );
  }

  function clearSearch() {
    setSearchTerm('');
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 shadow-sm sticky top-0 z-20">
        <div className="max-w-7xl mx-auto px-4 py-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between mb-4">
            <button
              onClick={() => setActiveComponent('OptionsAdmin')}
              className="flex items-center gap-2 text-slate-700 hover:text-brand-primary transition-colors font-montserrat font-semibold"
            >
              <ChevronLeft className="w-5 h-5" />
              <span className="text-sm sm:text-base">Volver</span>
            </button>

            <h1 className="text-xl sm:text-2xl font-extrabold text-brand-primary font-montserrat">
              Gestión de Entregas Provincia
            </h1>

            <div className="w-24" /> {/* Spacer for alignment */}
          </div>

          {/* Date Picker and Search */}
          <div className="flex flex-col sm:flex-row gap-3">
            {/* Date Picker */}
            <div className="relative flex-shrink-0">
              <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <input
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                className="w-full sm:w-auto pl-10 pr-4 py-2.5 border-2 border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary transition-all font-montserrat text-sm"
              />
            </div>

            {/* Search Bar */}
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Buscar por ID, nombre, teléfono, DNI, provincia..."
                className="w-full pl-10 pr-10 py-2.5 border-2 border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary transition-all font-montserrat text-sm"
              />
              {searchTerm && (
                <button
                  onClick={clearSearch}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  <X className="w-5 h-5" />
                </button>
              )}
            </div>
          </div>

          {/* Stats */}
          <div className="mt-4 flex items-center gap-4 text-sm font-montserrat">
            <div className="flex items-center gap-2">
              <Package className="w-4 h-4 text-brand-primary" />
              <span className="text-slate-600">
                Total: <span className="font-semibold text-slate-900">{deliveries.length}</span>
              </span>
            </div>
            {searchTerm && (
              <div className="text-slate-600">
                Mostrando: <span className="font-semibold text-slate-900">{filteredDeliveries.length}</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 py-6 sm:px-6 lg:px-8">
        {loading ? (
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-12">
            <div className="flex flex-col items-center justify-center">
              <Loader2 className="w-12 h-12 text-brand-primary animate-spin mb-4" />
              <p className="text-sm text-slate-600 font-montserrat">Cargando entregas...</p>
            </div>
          </div>
        ) : filteredDeliveries.length === 0 ? (
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-12">
            <div className="flex flex-col items-center justify-center">
              <Package className="w-16 h-16 text-slate-300 mb-4" />
              <h3 className="text-lg font-bold text-slate-900 font-montserrat mb-2">
                {searchTerm ? 'No se encontraron resultados' : 'No hay entregas'}
              </h3>
              <p className="text-sm text-slate-600 font-montserrat">
                {searchTerm
                  ? `No hay entregas que coincidan con "${searchTerm}"`
                  : 'No hay entregas a provincia para esta fecha'}
              </p>
              {searchTerm && (
                <button
                  onClick={clearSearch}
                  className="mt-4 px-4 py-2 bg-brand-primary text-white rounded-lg hover:bg-brand-primary/90 transition-colors font-montserrat text-sm"
                >
                  Limpiar búsqueda
                </button>
              )}
            </div>
          </div>
        ) : (
          <ProvinciaDeliveriesTable
            deliveries={filteredDeliveries}
            onDeliveryUpdate={handleDeliveryUpdate}
            onRefresh={fetchDeliveries}
          />
        )}
      </div>
    </div>
  );
}

export default AdminProvinciaDeliveries;